'use client'

import type { Data } from '@repo/strapi'
import AppLink from '@/components/elementary/AppLink'
import { CTAButtons } from '@/components/elementary/navbar/desktop/CTAButtons'
import { NavbarMenuPanel } from '@/components/elementary/navbar/desktop/NavbarMenuPanel'
import { Paragraph } from '@/components/typography/Paragraph'
import {
	NavigationMenu,
	NavigationMenuItem,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { NAVBAR_TEST_IDS } from '@/lib/test-ids'
import { SeoNavigationFallback } from '../SeoNavigationFallback'

interface DesktopNavigationProps {
	items?: Data.Component<'layout-utilities.navbar-item'>[] | null
	login?: Data.Component<'utilities.link'> | null
	buttons?: Data.Component<'utilities.link'>[] | null
	dropdown?: Data.Component<'layout-utilities.dropdown-item'> | null
	minimal?: boolean
}

export function DesktopNavigation({
	items,
	login,
	buttons,
	dropdown,
	minimal,
}: DesktopNavigationProps) {
	return (
		<>
			{/* SEO fallback navigation - screen reader only */}
			<SeoNavigationFallback items={items} login={login} buttons={buttons} dropdown={dropdown} />

			{/* Main visible navigation */}
			<NavigationMenu viewport={false} className="desktop-nav">
				<NavigationMenuList
					className="hidden lg:flex gap-8 items-center h-5.5"
					data-test-id={NAVBAR_TEST_IDS.NAVIGATION_ITEMS}
				>
					{/* Main navigation items */}
					{Array.isArray(items) &&
						items.map((item) => (
							<NavigationMenuItem key={item.id}>
								{item.href ? (
									<AppLink
										href={item.href}
										variant="primaryLink"
										data-test-id={NAVBAR_TEST_IDS.NAVIGATION_ITEM}
										openExternalInNewTab={item.newTab ?? false}
									>
										<Paragraph fontSize="bodyRegularMedium" className="text-inherit">
											{item.label}
										</Paragraph>
									</AppLink>
								) : (
									<NavigationMenuTrigger
										data-test-id={NAVBAR_TEST_IDS.NAVIGATION_ITEM}
										className={navigationMenuTriggerStyle()}
									>
										<Paragraph fontSize="bodyRegularMedium" className="text-inherit">
											{item.label}
										</Paragraph>
									</NavigationMenuTrigger>
								)}

								{item.columns && <NavbarMenuPanel columns={item.columns} />}
							</NavigationMenuItem>
						))}

					{/* CTA Buttons & Dropdown menu */}
					<CTAButtons login={login} buttons={buttons} dropdown={dropdown} minimal={minimal} />
				</NavigationMenuList>
			</NavigationMenu>
		</>
	)
}
